import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { GatewayName, PaymentResponse } from '../../PaymentAndSubcription/Types/PaymentType';

/**
 * What the receipt needs to know that the payment record does not carry.
 *
 * <p>Company identity is deliberately read live rather than snapshotted onto the payment row. A
 * receipt is a legal-ish artefact: if the company renames itself or changes its GST number six
 * months later, a receipt generated then should show the current details rather than silently
 * contradicting an old one.
 */
export interface ReceiptCompany {
  companyName?: string;
  gstNumber?: string;
  companyLogoUrl?: string;
  headquartersLocation?: string;
  website?: string;
}

/**
 * The per-provider wording on the receipt.
 *
 * <p>Every gateway names the same three things differently, and printing a Razorpay column header on
 * a Cashfree payment is the kind of small wrongness that makes a receipt look fabricated. Only the
 * labels change; the fields are always the same three.
 */
const GATEWAY_LABELS: Record<string, { provider: string; order: string; transaction: string; bank: string }> = {
  RAZORPAY: {
    provider: 'Razorpay',
    order: 'Razorpay Order ID',
    transaction: 'Razorpay Payment ID',
    bank: 'Bank Reference',
  },
  CASHFREE: {
    provider: 'Cashfree',
    order: 'Cashfree Order ID',
    transaction: 'Cashfree Payment ID',
    bank: 'Bank Reference',
  },
  PAYTM: {
    provider: 'Paytm',
    order: 'Paytm Order ID',
    transaction: 'Paytm Transaction ID',
    bank: 'Bank Reference',
  },
  PAYU: {
    provider: 'PayU',
    order: 'PayU Transaction ID',
    transaction: 'PayU Reference ID',
    bank: 'UTR / Bank Reference',
  },
};

const DEG = 0.35; // 100 paise in a metre-based doc: a visible but small corner radius.

/**
 * Draws and downloads the payment receipt.
 *
 * <p>Filenamed {@code Company_YYYY-MM-DD_PAY-<paymentId>.pdf} from the payment's own successful
 * timestamp rather than the current time, so a customer who downloads the receipt in March gets the
 * same filename they would have got in February.
 *
 * @returns the filename, so the caller can offer it again as a manual download without recomputing.
 */
export const generatePaymentReceiptPDF = (
  payment: PaymentResponse,
  company?: ReceiptCompany | null
): string => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const companyName = company?.companyName?.trim() || 'Nexgent';
  const gateway = (payment.gateway || 'RAZORPAY') as GatewayName;
  const labels = GATEWAY_LABELS[gateway] || GATEWAY_LABELS.RAZORPAY;

  const paidOn = payment.paymentCompletedAt ? new Date(payment.paymentCompletedAt) : new Date();
  const amount = Number(payment.amount ?? 0);
  const credits = Number(payment.creditsPurchased ?? 0);

  const filename = buildReceiptFilename(companyName, paidOn, payment.id);

  const colors = {
    primary: [79, 70, 229] as [number, number, number],
    success: [16, 185, 129] as [number, number, number],
    slate800: [30, 41, 59] as [number, number, number],
    slate500: [100, 116, 139] as [number, number, number],
    bgLight: [248, 250, 252] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
  };

  // 1. HEADER BAND
  doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.roundedRect(0, 0, pageWidth, 42, DEG, DEG, 'F');
  // Squared off the bottom so the band meets the page edge cleanly instead of floating.
  doc.rect(0, 30, pageWidth, 12, 'F');

  doc.setTextColor(colors.white[0], colors.white[1], colors.white[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('PAYMENT RECEIPT', 14, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    `Receipt No. ${payment.invoiceNumber || '-'}   |   ${labels.provider}   |   ${formatDate(paidOn)}`,
    14,
    29
  );

  // 2. AMOUNT PAID - the one thing a customer opens the document to find
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Amount Paid', 14, 52);
  doc.setFontSize(26);
  doc.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
  doc.text(formatInr(amount), 14, 64);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(colors.slate500[0], colors.slate500[1], colors.slate500[2]);
  doc.text('Paid in full', 14, 71);

  // 3. BILLED-TO / COMPANY BLOCK, right aligned
  const rightEdge = pageWidth - 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(colors.slate500[0], colors.slate500[1], colors.slate500[2]);
  doc.text('BILLED TO', rightEdge, 52, { align: 'right' });

  doc.setFontSize(11);
  doc.setTextColor(colors.slate800[0], colors.slate800[1], colors.slate800[2]);
  doc.text(companyName, rightEdge, 59, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(colors.slate500[0], colors.slate500[1], colors.slate500[2]);
  const companyLines = [
    company?.gstNumber ? `GSTIN: ${company.gstNumber}` : null,
    company?.headquartersLocation || null,
    company?.website || null,
  ].filter(Boolean) as string[];

  companyLines.forEach((line, index) => {
    doc.text(line, rightEdge, 65 + index * 10, { align: 'right' });
  });

  // 4. PAYMENT DETAILS
  let currentY = 84;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.text('PAYMENT DETAILS', 14, currentY);
  doc.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.line(14, currentY + 2, 44, currentY + 2);

  currentY += 6;

  const detailRows: [string, string][] = [
    ['Plan Purchased', payment.paymentPlan || '-'],
    ['Credits Purchased', credits ? `${credits} Job Credits` : '-'],
    ['Amount Paid', formatInr(amount)],
    ['Payment Status', (payment.paymentStatus || 'SUCCESS').toUpperCase()],
    ['Payment Date', formatDateTime(paidOn)],
    ['Paid By', payment.recruiterName || '-'],
    ['Payment ID', shortId(payment.id)],
    [labels.order, payment.gatewayOrderId || '-'],
    [labels.transaction, payment.gatewayPaymentId || payment.gatewayReference || '-'],
    [labels.bank, payment.bankReference || 'Not provided by the gateway'],
  ];

  autoTable(doc, {
    startY: currentY,
    head: [['Field', 'Value']],
    body: detailRows,
    theme: 'striped',
    headStyles: { fillColor: colors.slate800, textColor: colors.white, fontSize: 10 },
    styles: { fontSize: 9, cellPadding: 3.5 },
    columnStyles: {
      0: { cellWidth: 62, fontStyle: 'bold', textColor: colors.slate800 },
      1: { textColor: colors.slate500 },
    },
  });

  currentY = (doc as any).lastAutoTable.finalY + 14;

  // 5. AMOUNT SUMMARY
  // Built from the snapshot stored on the payment rather than assumed. This table used to print
  // "Subtotal = amount, Taxes = 0, Total = amount", which was true when no tax existed anywhere and
  // would now be a false statement on the document a customer claims input credit against. So the
  // rows are whatever was actually charged.
  //
  // A payment with no snapshot prints no tax line at all. "Tax was never applied to this payment"
  // and "tax was applied and came to nil" are different statements, and only the second one earns
  // a GST line.
  const hasTaxSnapshot = payment.taxTotalAmount !== null && payment.taxTotalAmount !== undefined;
  const taxRows: string[][] = [];

  if (hasTaxSnapshot) {
    taxRows.push(['Price before tax', formatInr(payment.subtotalAmount ?? amount)]);

    if ((payment.taxCgstAmount ?? 0) > 0) {
      taxRows.push([`CGST (${payment.taxCgstPercent ?? 0}%)`, formatInr(payment.taxCgstAmount ?? 0)]);
    }
    if ((payment.taxSgstAmount ?? 0) > 0) {
      taxRows.push([`SGST (${payment.taxSgstPercent ?? 0}%)`, formatInr(payment.taxSgstAmount ?? 0)]);
    }
    if ((payment.taxIgstAmount ?? 0) > 0) {
      taxRows.push([`IGST (${payment.taxIgstPercent ?? 0}%)`, formatInr(payment.taxIgstAmount ?? 0)]);
    }

    taxRows.push(['Total GST', formatInr(payment.taxTotalAmount ?? 0)]);
  } else {
    taxRows.push(['Subtotal', formatInr(amount)]);
  }

  taxRows.push(['Total Paid', formatInr(amount)]);

  // The total row is the point of the table, so it gets the accent fill rather than the striped
  // banding every other row has. Its index is computed because the number of rows above it now
  // varies with the tax breakdown.
  const totalRowIndex = taxRows.length - 1;

  autoTable(doc, {
    startY: currentY,
    body: taxRows,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3.5 },
    columnStyles: {
      0: { cellWidth: 132, fontStyle: 'bold', fillColor: colors.bgLight },
      1: { halign: 'right', fontStyle: 'bold', textColor: colors.primary },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.index === totalRowIndex) {
        data.cell.styles.fillColor = colors.primary;
        data.cell.styles.textColor = colors.white;
        data.cell.styles.fontSize = 10;
      }
    },
  });

  currentY = (doc as any).lastAutoTable.finalY + 16;

  // 6. NOTE
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(colors.slate500[0], colors.slate500[1], colors.slate500[2]);
  const note = doc.splitTextToSize(
    'This receipt was generated by Nexgent and reflects the payment record held on the platform at the time of '
    + 'generation. For any query, quote the receipt number above. Refunds, if any, are issued against the original '
    + `transaction reference recorded by ${labels.provider}.`,
    pageWidth - 28
  );
  doc.text(note, 14, currentY);

  // 7. FOOTER
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(colors.slate500[0], colors.slate500[1], colors.slate500[2]);
    doc.text(`Computer generated receipt - no signature required`, 14, pageHeight - 10);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
  }

  doc.save(filename);

  return filename;
};

/**
 * {@code Company_YYYY-MM-DD_PAY-<paymentId>.pdf}.
 *
 * <p>The date is local, not UTC, so a payment made at 11pm is not stamped with tomorrow's date
 * because of where the server happens to be running.
 */
export const buildReceiptFilename = (
  companyName: string,
  paidOn: Date,
  paymentId: string
): string => {
  const safeCompany = (companyName || 'Nexgent').trim().replace(/[\\/:*?"<>|]+/g, '-') || 'Nexgent';
  const yyyy = String(paidOn.getFullYear()).padStart(4, '0');
  const mm = String(paidOn.getMonth() + 1).padStart(2, '0');
  const dd = String(paidOn.getDate()).padStart(2, '0');
  const id = (paymentId || 'unknown').replace(/[^A-Za-z0-9-]/g, '');

  return `${safeCompany}_${yyyy}-${mm}-${dd}_PAY-${id}.pdf`;
};

const formatInr = (amount: number): string => {
  const safe = Number.isFinite(amount) ? amount : 0;
  return `INR ${safe.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatDateTime = (date: Date): string =>
  `${formatDate(date)}, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;

/**
 * UUIDs are 36 characters and wrap badly in a table cell, and no one reads one in full to check it
 * against a receipt. The first segment is enough to recognise and is what a support ticket quotes.
 */
const shortId = (id?: string | null): string => {
  if (!id) {
    return '-';
  }
  return id.length > 13 ? `${id.slice(0, 13)}...` : id;
};